import { getTenantsBasePath } from "metabase/common/tenants";
import type {
  BaseUser,
  CustomVizPluginId,
  DatabaseId,
  FieldId,
  GroupId,
  SchemaName,
  SegmentId,
  TableId,
} from "metabase-types/api";

export const isInternalUser = (user: Pick<BaseUser, "tenant_id">) =>
  user.tenant_id == null;

export function newUser() {
  return `/admin/people/new`;
}

export function newTenantUser() {
  return `${getTenantsBasePath()}/people/new`;
}

export function editUser(user: Pick<BaseUser, "id" | "tenant_id">) {
  return isInternalUser(user)
    ? `/admin/people/${user.id}/edit`
    : `${getTenantsBasePath()}/people/${user.id}/edit`;
}

export function resetPassword(user: BaseUser) {
  return isInternalUser(user)
    ? `/admin/people/${user.id}/reset`
    : `${getTenantsBasePath()}/people/${user.id}/reset`;
}

export function newUserSuccess(user: BaseUser) {
  return isInternalUser(user)
    ? `/admin/people/${user.id}/success`
    : `${getTenantsBasePath()}/people/${user.id}/success`;
}

export function deactivateUser(user: BaseUser) {
  return isInternalUser(user)
    ? `/admin/people/${user.id}/deactivate`
    : `${getTenantsBasePath()}/people/${user.id}/deactivate`;
}

export function reactivateUser(user: BaseUser) {
  return isInternalUser(user)
    ? `/admin/people/${user.id}/reactivate`
    : `${getTenantsBasePath()}/people/${user.id}/reactivate`;
}

export function unsubscribeUser(user: Pick<BaseUser, "id" | "tenant_id">) {
  return isInternalUser(user)
    ? `/admin/people/${user.id}/unsubscribe`
    : `${getTenantsBasePath()}/people/${user.id}/unsubscribe`;
}

export function adminGroup(groupId: GroupId) {
  return `/admin/people/groups/${groupId}`;
}

// TODO: move to EE urls

export function newDatabase() {
  return `/admin/databases/create`;
}

export function viewDatabases() {
  return `/admin/databases`;
}

export function viewDatabase(databaseId: DatabaseId) {
  return `/admin/databases/${databaseId}`;
}

export function editDatabase(databaseId: DatabaseId) {
  return `/admin/databases/${databaseId}/edit`;
}

export function editDatabaseWritableConnection(databaseId: DatabaseId) {
  return `/admin/databases/${databaseId}/write-data`;
}

type DataModelParams = {
  databaseId?: DatabaseId;
  schemaName?: SchemaName | null;
  tableId?: TableId;
  fieldId?: FieldId;
};

export function dataModel({
  databaseId,
  schemaName,
  tableId,
  fieldId,
}: DataModelParams = {}) {
  const parts = ["/admin/datamodel"];

  if (databaseId != null) {
    parts.push("database", String(databaseId));

    if (schemaName != null) {
      const schemaId = `${databaseId}:${encodeURIComponent(schemaName)}`;
      parts.push("schema", schemaId);

      if (tableId != null) {
        parts.push("table", String(tableId));

        if (fieldId != null) {
          parts.push("field", String(fieldId));
        }
      }
    }
  }

  return parts.join("/");
}

export type DataModelSegmentsParams = {
  tableId?: TableId;
};

export function dataModelSegments({ tableId }: DataModelSegmentsParams = {}) {
  const params = new URLSearchParams();
  if (tableId != null) {
    params.set("table", String(tableId));
  }

  const baseUrl = "/admin/datamodel/segments";
  const queryString = params.toString();
  return queryString.length > 0 ? `${baseUrl}?${queryString}` : baseUrl;
}

export function newDataModelSegment() {
  return "/admin/datamodel/segment/create";
}

export function dataModelSegment(segmentId: SegmentId) {
  return `/admin/datamodel/segment/${segmentId}`;
}

export function dataModelSegmentRevisions(segmentId: SegmentId) {
  return `${dataModelSegment(segmentId)}/revisions`;
}

export function generalSettings() {
  return "/admin/settings/general";
}

export function domainsSettings() {
  return "/admin/settings/domains";
}

export function uploadsSettings() {
  return "/admin/settings/uploads";
}

export function adminLicense() {
  return "/admin/settings/license";
}

export function customViz() {
  return "/admin/settings/custom-visualizations";
}

export function customVizAdd() {
  return `${customViz()}/new`;
}

export function customVizEdit(id: CustomVizPluginId | undefined) {
  return `${customViz()}/edit/${id ?? ""}`;
}

export function customVizDev() {
  return `${customViz()}/development`;
}

export function adminHelp() {
  return "/admin/help";
}

export function adminHelpGrantAccess() {
  return `${adminHelp()}/grant-access`;
}

export function adminAiSettings() {
  return "/admin/metabot";
}

export function adminMcpSettings() {
  return `${adminAiSettings()}/mcp`;
}
